import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Plus, Clock, Pencil, Trash2, ScrollText, LayoutDashboard, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const VERTICALES = ['Todas', 'Salud', 'Vivienda', 'Emprendimiento', 'Alimentación', 'Educación', 'Vestimenta'];

function TandaCard({ tanda }) {
  return (
    <Card data-testid={`tanda-card-${tanda._id}`} className="hover:shadow-lg transition-all cursor-pointer group border-l-4 border-l-primary">
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <span className="text-xs uppercase font-bold text-muted-foreground tracking-wider">{tanda.area}</span>
            <CardTitle className="text-xl mt-1 group-hover:text-primary transition-colors">{tanda.name}</CardTitle>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-secondary-foreground">${tanda.total_amount}</span>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 text-sm text-muted-foreground">
          <div className="flex justify-between">
            <span>Aportación:</span>
            <span className="font-medium text-foreground">${tanda.contribution_amount} / {tanda.periodicity}</span>
          </div>
          <div className="flex justify-between">
            <span>Participantes:</span>
            <span className="font-medium text-foreground">{tanda.members?.length || 0} / {tanda.max_participants}</span>
          </div>
          <div className="flex justify-between">
            <span>Inicio:</span>
            <span className="font-medium text-foreground">{new Date(tanda.start_date).toLocaleDateString()}</span>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Link to={`/tandas/${tanda._id}`} className="w-full">
          <Button data-testid={`view-tanda-btn-${tanda._id}`} className="w-full rounded-full" variant="secondary">Ver Detalles</Button>
        </Link>
      </CardFooter>
    </Card>
  );
}

function TandaGrid({ tandas }) {
  const [filtro, setFiltro] = useState('Todas');
  const filtered = filtro === 'Todas' ? tandas : tandas.filter(t => (t.area || '').startsWith(filtro));

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap gap-2" data-testid="vertical-filters">
        {VERTICALES.map(v => (
          <button
            key={v}
            data-testid={`filter-${v.toLowerCase()}`}
            onClick={() => setFiltro(v)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
              filtro === v ? 'bg-primary text-primary-foreground border-primary' : 'bg-white/60 dark:bg-white/5 border-border hover:bg-primary/10'
            }`}
          >
            {v}
          </button>
        ))}
      </div>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map(tanda => <TandaCard key={tanda._id} tanda={tanda} />)}
      </div>
      {filtered.length === 0 && (
        <p className="text-center text-muted-foreground py-10" data-testid="no-tandas-message">
          No hay tandas en la vertical <strong>{filtro}</strong> por ahora.
        </p>
      )}
    </section>
  );
}

function AdminPanel({ tandas, pendingPayments, onValidate, onDelete }) {
  const navigate = useNavigate();

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <Link to="/admin/create-tanda">
          <Button data-testid="admin-create-tanda-btn" className="rounded-full gap-2"><Plus size={18} /> Crear Tanda</Button>
        </Link>
        <Link to="/admin/audit">
          <Button data-testid="admin-audit-btn" variant="outline" className="rounded-full gap-2"><ScrollText size={18} /> Auditoría</Button>
        </Link>
      </div>

      {/* Pagos pendientes */}
      <section className="space-y-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Clock className="text-yellow-500" /> Pagos Pendientes de Validación
        </h2>
        {pendingPayments.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="no-pending-payments">No hay comprobantes por validar. 🎉</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {pendingPayments.map(p => (
              <Card key={p._id} data-testid={`pending-payment-${p._id}`} className="border-yellow-200 bg-yellow-50 dark:bg-yellow-900/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{p.user?.full_name}</CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-1">
                  <p><strong>Tanda:</strong> {p.tanda?.name}</p>
                  <p><strong>Monto:</strong> ${p.amount}</p>
                  <p><strong>Ciclo:</strong> {p.week_number}</p>
                  <a href={p.proof_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline" data-testid={`payment-proof-link-${p._id}`}>
                    Ver Comprobante
                  </a>
                </CardContent>
                <CardFooter className="gap-2">
                  <Button size="sm" variant="outline" data-testid={`reject-payment-${p._id}`} className="w-full border-red-200 hover:bg-red-100 text-red-600" onClick={() => onValidate(p._id, false)}>Rechazar</Button>
                  <Button size="sm" data-testid={`validate-payment-${p._id}`} className="w-full bg-green-500 hover:bg-green-600 text-white" onClick={() => onValidate(p._id, true)}>Validar</Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Gestión de tandas */}
      <section className="space-y-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <ShieldCheck className="text-primary" /> Gestión de Tandas
        </h2>
        <div className="space-y-3">
          {tandas.map(t => (
            <div key={t._id} data-testid={`manage-tanda-row-${t._id}`} className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl bg-white/60 dark:bg-white/5 border border-border">
              <div className="min-w-0">
                <p className="font-semibold">{t.name}</p>
                <p className="text-xs text-muted-foreground">
                  <Badge variant="outline" className="mr-2">{t.area}</Badge>
                  {t.members?.length || 0}/{t.max_participants} participantes · ${t.contribution_amount}/{t.periodicity}
                </p>
              </div>
              <div className="flex gap-2">
                <Link to={`/tandas/${t._id}`}>
                  <Button size="sm" variant="secondary" className="rounded-full">Ver</Button>
                </Link>
                <Button size="sm" variant="outline" data-testid={`edit-tanda-btn-${t._id}`} className="rounded-full gap-1" onClick={() => navigate(`/admin/edit-tanda/${t._id}`)}>
                  <Pencil size={14} /> Editar
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid={`delete-tanda-btn-${t._id}`} className="rounded-full gap-1 text-red-600 border-red-200 hover:bg-red-50">
                      <Trash2 size={14} /> Eliminar
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Eliminar "{t.name}"?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta acción es irreversible. Se eliminarán también las solicitudes de participación y los comprobantes de pago asociados.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="cancel-delete-tanda">Cancelar</AlertDialogCancel>
                      <AlertDialogAction data-testid="confirm-delete-tanda" className="bg-red-600 hover:bg-red-700" onClick={() => onDelete(t._id)}>
                        Sí, eliminar
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
          {tandas.length === 0 && <p className="text-sm text-muted-foreground">Aún no hay tandas creadas.</p>}
        </div>
      </section>
    </div>
  );
}

export default function Dashboard() {
  const { user, api } = useAuth();
  const [tandas, setTandas] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);

  useEffect(() => {
    fetchTandas();
    if (user?.role === 'admin') fetchPendingPayments();
  }, [user]);

  const fetchTandas = async () => {
    try {
      const { data } = await api.get('/tandas/');
      setTandas(data);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPendingPayments = async () => {
    try {
      const { data } = await api.get('/tandas/payments/pending');
      setPendingPayments(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleValidatePayment = async (paymentId, valid) => {
    try {
      await api.post(`/tandas/payments/${paymentId}/validate?valid=${valid}`);
      toast.success(valid ? 'Pago validado' : 'Pago rechazado');
      fetchPendingPayments();
    } catch (error) {
      toast.error('Error al validar pago');
    }
  };

  const handleDeleteTanda = async (tandaId) => {
    try {
      await api.delete(`/tandas/${tandaId}`);
      toast.success('Tanda eliminada');
      fetchTandas();
    } catch (error) {
      toast.error('Error al eliminar la tanda');
    }
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-heading font-bold">Dashboard</h1>
      </div>

      {isAdmin ? (
        <Tabs defaultValue="comunidad" data-testid="admin-tabs">
          <TabsList className="rounded-full">
            <TabsTrigger value="comunidad" data-testid="tab-comunidad" className="rounded-full gap-2">
              <LayoutDashboard size={16} /> Vista Comunidad
            </TabsTrigger>
            <TabsTrigger value="admin" data-testid="tab-admin" className="rounded-full gap-2">
              <ShieldCheck size={16} /> Vista Admin
            </TabsTrigger>
          </TabsList>
          <TabsContent value="comunidad" className="mt-6">
            <TandaGrid tandas={tandas} />
          </TabsContent>
          <TabsContent value="admin" className="mt-6">
            <AdminPanel tandas={tandas} pendingPayments={pendingPayments} onValidate={handleValidatePayment} onDelete={handleDeleteTanda} />
          </TabsContent>
        </Tabs>
      ) : (
        <TandaGrid tandas={tandas} />
      )}
    </div>
  );
}
